import { useMutation } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";
import type { ClientDocumentRecord } from "./documents";

/**
 * Generador de Documentos: arma PDFs membreteados reales (con el diseño
 * navy/dorado del rediseño visual) a partir de datos reales — nunca datos
 * de relleno. Reemplaza el formato plano anterior de "carta de cobro"
 * (.docx sin membrete) y agrega Factura/Expediente jurídico/Informe.
 */

export type DocGeneratorType =
  | "CUENTA_COBRO"
  | "FACTURA"
  | "JURIDICO"
  | "INFORME"
  | "PODER_ESPECIAL"
  | "ACTA_CONCILIACION"
  | "REQUERIMIENTO_PAGO"
  | "CONTRATO_SERVICIOS"
  | "CERTIFICACION_LABORAL";

export const DOC_TYPE_LABEL: Record<DocGeneratorType, string> = {
  CUENTA_COBRO: "Cuenta de cobro",
  FACTURA: "Factura",
  JURIDICO: "Expediente jurídico",
  INFORME: "Informe",
  PODER_ESPECIAL: "Poder especial",
  ACTA_CONCILIACION: "Acta de conciliación",
  REQUERIMIENTO_PAGO: "Requerimiento de pago",
  CONTRATO_SERVICIOS: "Contrato de servicios",
  CERTIFICACION_LABORAL: "Certificación laboral",
};

// CUENTA_COBRO admite dos formas (desde una factura real, o manual con
// clientId+concepto+valor) — ver el mismo comentario en
// documentGenerator.validators.ts del backend sobre por qué esto no es un
// discriminated union estricto. REQUERIMIENTO_PAGO reutiliza el mismo patrón.
export interface DocGeneratorPayload {
  docType: DocGeneratorType;
  clientId?: string;
  concepto?: string;
  valor?: number;
  invoiceId?: string;
  legalCaseId?: string;
  ultimaActuacion?: string;
  proximaActuacion?: string;
  periodo?: string;
  resumen?: string;
  indicadores?: Array<{ label: string; valor: string }>;
  analisis?: string;
  hallazgos?: string;
  recomendaciones?: string;
  apoderadoNombre?: string;
  apoderadoTarjetaProfesional?: string;
  facultades?: string;
  lugar?: string;
  hechos?: string;
  acuerdos?: string;
  compromisos?: string;
  plazoDias?: number;
  advertencia?: string;
  objeto?: string;
  valorHonorarios?: number;
  formaPago?: string;
  vigencia?: string;
  clausulasAdicionales?: string;
  employeeId?: string;
  dirigidoA?: string;
  proposito?: string;
}

export function usePreviewGeneratedDocument() {
  return useMutation({
    mutationFn: async (payload: DocGeneratorPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ html: string }>>("/document-generator/preview", payload);
      return data.data.html;
    },
  });
}

export function useGenerateDocument() {
  return useMutation({
    mutationFn: async (payload: DocGeneratorPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ document: ClientDocumentRecord; numeroLabel: string }>>(
        "/document-generator/generate",
        payload
      );
      return data.data;
    },
  });
}
